import { EditActions } from '@/components';
import { useEditableDetail } from '@/hooks';
import { cn, NOOP } from '@/utils';
import React, { memo, useEffect, useRef } from 'react';

import { Input } from 'antd';
import type { TextAreaProps } from 'antd/es/input';
import { TextAreaRef } from 'antd/es/input/TextArea';

import './styles.scss';

const { TextArea: TextAreaAntd } = Input;

export interface ITextareaProps extends TextAreaProps {
	label?: string;
	labelClassName?: string;
	inputClassName?: string;
	valueClassName?: string;
	containerClassName?: string;
	require?: boolean;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	horizontal?: boolean;
	resize?: boolean;
	isDetail?: boolean;
}
const Textarea: React.FC<ITextareaProps> = ({
	label,
	labelClassName = '',
	require,
	inputClassName = '',
	valueClassName = '',
	touched,
	errorCode,
	hasServerError,
	horizontal = true,
	containerClassName = '',
	resize = false,
	rows = 4,
	maxLength = 1000,
	showCount,
	isDetail = false,
	...rest
}) => {
	const { isUpdate, editable, handleLabelClick, onCancel, onSave } =
		useEditableDetail({ fieldName: rest.name as string });
	const canResize = resize ? 'vertical' : 'none';

	const textAreaRef = useRef<TextAreaRef>(null);

	useEffect(() => {
		if (editable && isUpdate && isDetail) {
			textAreaRef?.current?.focus();
		}
	}, [editable]);

	return (
		<div
			className={cn(
				'flex flex-1 gap-2 text-area',
				horizontal ? 'items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2 min-w-[110px]',
						labelClassName
					)}
				>
					{label} {require && <span className="text-red6">*</span>}
				</label>
			)}

			{isDetail && !editable ? (
				<div className="w-full">
					<div
						className={cn(
							'box-border w-full text-[13px] md:text-[14px] h-[64px] xl:h-[118px] text-font2 py-[7px] xl:px-[11px] whitespace-pre-wrap text-left xl:hover:border xl:hover:border-gray1 rounded-[3px] cursor-text overflow-auto break-words',
							rest.disabled && 'cursor-not-allowed hover:bg-porcelain',
							valueClassName
						)}
						onClick={!rest.disabled ? handleLabelClick : undefined}
					>
						{rest.value?.toString() || '-'}
					</div>
				</div>
			) : (
				<div className="relative w-full h-auto">
					<TextAreaAntd
						{...rest}
						autoComplete="new-password"
						size="large"
						className={cn(
							'bg-white w-full text-[13px] text-font2 !h-[64px] xl:!h-[118px] xl:rounded-none',
							inputClassName
						)}
						status={(touched && errorCode) || hasServerError ? 'error' : ''}
						rows={rows}
						maxLength={maxLength}
						style={{ resize: canResize }}
						showCount={showCount}
						classNames={{
							count: 'text-nobel text-[10px] !bottom-[-5px] !right-[5px]',
						}}
						onPressEnter={isDetail ? NOOP : rest.onPressEnter}
						onBlur={() => {
							window.scrollTo(0, 0);
							isDetail ? NOOP : rest.onBlur;
						}}
						ref={textAreaRef}
					/>
					{isUpdate && isDetail && (
						<EditActions
							onCancel={onCancel}
							onSave={onSave}
							className={cn(
								'absolute -bottom-5 right-[-11px]  border border-gray1 p-[5px] gap-2 bg-white z-10 h-[28px]'
							)}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(Textarea);
